import React, { useState, useEffect, useRef } from 'react';
import { useBag } from '../context/BagContext';
import { useAuth } from '../context/AuthContext';
import { usePayment } from '../context/PaymentContext';
import { useAddress } from '../context/AddressContext';
import { useNavigate, Link } from 'react-router-dom';
import { cartService, settingsService } from '../services/api';
import {
  CONTACT_CHANNELS_UPDATED_EVENT,
  parseContactChannelsFromStorage,
  normalizeContactChannelsData,
  computeShippingFromSubtotal,
  buildWhatsAppUrl,
  getWhatsAppOrderPhoneDigits,
} from '../utils/whatsappContact';
import { getApiBaseUrl } from '../config/environment';
import ConfirmationModal from '../components/ConfirmationModal';
import CheckoutGuestAccountForm from '../components/CheckoutGuestAccountForm';
import SiteLoading from '../components/SiteLoading';
import { PLACEHOLDER_NO_IMAGE_SMALL } from '../utils/placeholderImage';
import { isDefaultColorLabel } from '../utils/productColorLabels';
import { mergeGuestCartToApi } from '../utils/mergeGuestCartToApi';
import './BagPage.css';

/** WhatsApp order summary from shopping bag (guest or logged-in); mirrors checkout message shape. */
function buildBagWhatsAppOrderMessage({
  user,
  bagItems,
  subtotal,
  saleDiscount,
  couponDiscount,
  shipping,
  total,
  deliveryInfo,
  isGuest,
}) {
  const lines = [];
  lines.push('🛍️ *ORDER VIA WHATSAPP (SHOPPING BAG)* 🛍️');
  lines.push('');
  if (isGuest) {
    lines.push('*Guest* — I’m ordering from the website without logging in. Please confirm details in chat.');
    lines.push('');
  } else if (user?.name || user?.email || user?.phone) {
    lines.push('*Customer*');
    if (user.name) lines.push(`Name: ${user.name}`);
    if (user.email) lines.push(`Email: ${user.email}`);
    if (user.phone) lines.push(`Phone: ${user.phone}`);
    lines.push('');
  }

  lines.push('*Items*');
  (bagItems || []).forEach((item, i) => {
    const name = item.name || 'Product';
    const qty = item.quantity || 1;
    const price = Number(item.price) || 0;
    const lineTotal = price * qty;
    const color =
      item.selectedColor && !isDefaultColorLabel(item.selectedColor)
        ? item.selectedColor
        : null;
    const size =
      item.selectedSize && String(item.selectedSize).toLowerCase() !== 'default'
        ? item.selectedSize
        : null;
    lines.push(`${i + 1}) ${name}`);
    lines.push(
      `   Qty: ${qty} × PKR ${price.toLocaleString()} = PKR ${lineTotal.toLocaleString()}`
    );
    const specs = [color ? `Colour: ${color}` : '', size ? `Size: ${size}` : '']
      .filter(Boolean)
      .join(' | ');
    if (specs) lines.push(`   ${specs}`);
  });
  lines.push('');

  lines.push('*Summary*');
  lines.push(`Items subtotal: PKR ${Number(subtotal).toLocaleString()}`);
  if (saleDiscount > 0) {
    lines.push(`Sale discount: -PKR ${Number(saleDiscount).toLocaleString()}`);
  }
  if (couponDiscount > 0) {
    lines.push(`Coupon discount: -PKR ${Number(couponDiscount).toLocaleString()}`);
  }
  const shipLabel = deliveryInfo?.qualified
    ? 'FREE'
    : `PKR ${Number(shipping).toLocaleString()}`;
  lines.push(`Shipping (${shipLabel})`);
  lines.push(`*TOTAL: PKR ${Number(total).toLocaleString()}*`);
  lines.push('');
  lines.push('Please confirm this order. I’m sending this from my shopping bag on the website.');
  if (typeof window !== 'undefined' && window.location?.href) {
    lines.push('');
    lines.push(`Page: ${window.location.href}`);
  }
  return lines.join('\n');
}

const BagPage = () => {
  // Use useBag hook (same as Header) for guest cart
  const { 
    bagItems: guestBagItems, 
    updateQuantity: updateGuestQuantity, 
    removeFromBag: removeGuestItem, 
    bagTotal: guestBagTotal, 
    clearBag: clearGuestBag,
  } = useBag();
  
  const { isAuthenticated, user } = useAuth();
  const { 
    getActivePaymentMethods, 
    selectedPaymentMethod, 
    setSelectedPaymentMethod, 
    getSelectedPaymentMethod 
  } = usePayment();
  const { getUserAddresses, getDefaultAddress } = useAddress();
  const navigate = useNavigate();

  // State for real-time cart (authenticated users)
  const [cart, setCart] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [updatingItem, setUpdatingItem] = useState(null);
  const [removingItem, setRemovingItem] = useState(null);
  const [cartReady, setCartReady] = useState(false); // Flag to prevent showing partial cart data
  
  // State for cart summary from API
  const [cartSummary, setCartSummary] = useState(null);
  const [loadingSummary, setLoadingSummary] = useState(false);
  
  // Refs to prevent duplicate fetches and renders
  const fetchingRef = useRef(false);
  const mountedRef = useRef(true);
  const cartInitializedRef = useRef(false);
  const lastCartIdRef = useRef(null);
  const lastCartItemsCountRef = useRef(0);

  // State for UI
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [itemToRemove, setItemToRemove] = useState(null);
  const [selectedAddress, setSelectedAddress] = useState(null);

  const [storeContact, setStoreContact] = useState(() =>
    normalizeContactChannelsData(parseContactChannelsFromStorage() || {})
  );

  useEffect(() => {
    let cancelled = false;
    settingsService.getContactChannels().then((res) => {
      if (cancelled) return;
      const d = res?.data || res;
      if (d && typeof d === 'object') {
        setStoreContact(normalizeContactChannelsData(d));
      }
    });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const sync = () => {
      const ch = parseContactChannelsFromStorage();
      if (ch) setStoreContact(normalizeContactChannelsData(ch));
    };
    window.addEventListener(CONTACT_CHANNELS_UPDATED_EVENT, sync);
    window.addEventListener('storage', sync);
    return () => {
      window.removeEventListener(CONTACT_CHANNELS_UPDATED_EVENT, sync);
      window.removeEventListener('storage', sync);
    };
  }, []);

  // Get user addresses and set default
  const userAddresses = user ? getUserAddresses(user.id) : [];
  const defaultAddress = user ? getDefaultAddress(user.id) : null;

  // Set default address when component mounts or user changes
  useEffect(() => {
    if (defaultAddress && !selectedAddress) {
      setSelectedAddress(defaultAddress);
    }
  }, [defaultAddress, selectedAddress]);

  // Cleanup on unmount
  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Fetch cart from API for authenticated users only
  // CRITICAL: Only fetch once per mount/auth change
  useEffect(() => {
    // Reset when auth changes
    if (!isAuthenticated) {
      cartInitializedRef.current = false;
      lastCartIdRef.current = null;
      lastCartItemsCountRef.current = 0;
      setCart(null);
      setCartReady(false);
      setLoading(false);
      fetchingRef.current = false;
      return;
    }

    // CRITICAL: Prevent duplicate fetches - Check this FIRST
    if (fetchingRef.current) {
      return;
    }

    // CRITICAL: Prevent re-fetching if cart is already initialized
    if (cartInitializedRef.current) {
      // But ensure cartReady is set if cart exists
      if (cart && cart._id === lastCartIdRef.current && !cartReady) {
        setCartReady(true);
      }
      return;
    }

    // CRITICAL: Prevent fetch if cart is already loaded and ready
    if (cart && cart._id && cartReady) {
      return;
    }

    const fetchCart = async () => {
      if (!isAuthenticated) {
        setTimeout(() => {
          if (mountedRef.current) {
            setLoading(false);
          }
        }, 100);
        return;
      }

      // Set fetching flag IMMEDIATELY
      fetchingRef.current = true;

      try {
        if (mountedRef.current) {
          setLoading(true);
          setError(null);
        }

        await mergeGuestCartToApi(clearGuestBag, () => guestBagItems);

        const response = await cartService.getCart();
        
        if (!mountedRef.current) {
          fetchingRef.current = false;
          return;
        }
        
        // Handle version conflict errors silently - refresh cart
        if (!response.success && response.error) {
          const errorMessage = response.error || response.message || '';
          const isVersionConflict = errorMessage.includes('No matching document found') || 
                                    errorMessage.includes('version') ||
                                    errorMessage.includes('modifiedPaths');
          
          if (isVersionConflict) {
            // Retry once with delay
            await new Promise(resolve => setTimeout(resolve, 300));
            const retryResponse = await cartService.getCart();
            if (retryResponse.success && retryResponse.data) {
              // Use retry response
              const cartData = {
                ...retryResponse.data,
                items: Array.isArray(retryResponse.data.items) ? retryResponse.data.items : []
              };
              lastCartIdRef.current = cartData._id;
              lastCartItemsCountRef.current = cartData.items.length;
              setCart(cartData);
              setCartReady(true);
              fetchingRef.current = false;
              if (mountedRef.current) {
                setLoading(false);
              }
              return;
            }
          }
        }
        
        if (response.success && response.data) {
          // Frontend deduplication: Remove duplicate items before setting state
          const rawItems = Array.isArray(response.data.items) ? response.data.items : [];

          // CRITICAL: Validate items before processing
          // Filter out invalid items (missing product, invalid data, etc.)
          const validItems = rawItems.filter(item => {
            // Must have _id
            if (!item._id) {
              console.warn('⚠️ Item without _id, skipping:', item);
              return false;
            }
            
            // Must have product
            if (!item.product) {
              console.warn('⚠️ Item without product, skipping:', item._id);
              return false;
            }
            
            // Product must have _id or id
            const productId = item.product._id || item.product.id;
            if (!productId) {
              console.warn('⚠️ Item product without _id, skipping:', item._id);
              return false;
            }
            
            // Must have valid quantity
            if (!item.quantity || item.quantity < 1) {
              console.warn('⚠️ Item with invalid quantity, skipping:', item._id, item.quantity);
              return false;
            }
            
            return true;
          });
          
          if (validItems.length !== rawItems.length) {
            console.warn(`⚠️ Filtered out ${rawItems.length - validItems.length} invalid items from API response`);
          }
          
          // Deduplicate by item._id (cart item ID, not product ID)
          const seenItemIds = new Set();
          const uniqueItems = validItems.filter(item => {
            const itemId = item._id;
            if (seenItemIds.has(itemId)) {
              console.warn('⚠️ Duplicate cart item _id detected:', itemId, item.product?.name);
              return false;
            }
            seenItemIds.add(itemId);
            return true;
          });
          
          // Also check for same product+color+size combination (should be merged, not duplicated)
          // CRITICAL: If same product+color+size appears multiple times, merge quantities
          const seenCombinations = new Map(); // Map to track and merge items
          const finalItems = [];
          
          uniqueItems.forEach(item => {
            const productId = item.product?._id || item.product?.id;
            const color = item.color || 'default';
            const size = item.size || 'default';
            const combination = `${productId}-${color}-${size}`;
            
            if (seenCombinations.has(combination)) {
              // Duplicate combination found - merge quantities
              const existingItem = seenCombinations.get(combination);
              const mergedQuantity = existingItem.quantity + item.quantity;
              const mergedTotal = (existingItem.total || 0) + (item.total || 0);
              
              console.warn('⚠️ Duplicate product+color+size combination detected - MERGING:', {
                combination,
                productName: item.product?.name,
                existingItemId: existingItem._id,
                duplicateItemId: item._id,
                existingQuantity: existingItem.quantity,
                duplicateQuantity: item.quantity,
                mergedQuantity: mergedQuantity,
                action: 'Merging quantities, keeping first item'
              });
              
              // Update the existing item with merged quantity
              existingItem.quantity = mergedQuantity;
              existingItem.total = mergedTotal;
              // Don't add this item to finalItems, it's already there via existingItem
    } else {
              // First occurrence - add to map and finalItems
              seenCombinations.set(combination, item);
              finalItems.push(item);
            }
          });
          
          // CRITICAL: Log if items were removed
          if (finalItems.length < rawItems.length) {
            console.warn(`⚠️ BACKEND ISSUE: API returned ${rawItems.length} items, but after validation/deduplication only ${finalItems.length} items remain.`, {
              removed: rawItems.length - finalItems.length,
              originalItems: rawItems.map(item => ({
                itemId: item._id,
                productName: item.product?.name,
                productId: item.product?._id || item.product?.id,
                color: item.color,
                size: item.size
              })),
              finalItems: finalItems.map(item => ({
                itemId: item._id,
                productName: item.product?.name,
                productId: item.product?._id || item.product?.id,
                color: item.color,
                size: item.size
              }))
            });
          }
          
          const cartData = {
            ...response.data,
            items: finalItems
          };
          
          // Create stable identifier for this cart
          const cartId = cartData._id;
          const itemsCount = cartData.items.length;

          // Check if this is the same cart we already have
          if (lastCartIdRef.current === cartId && lastCartItemsCountRef.current === itemsCount && cartReady) {
            fetchingRef.current = false;
            cartInitializedRef.current = true;
            if (mountedRef.current) {
              setLoading(false);
              if (!cartReady) {
                setCartReady(true);
              }
            }
            return;
          }
          
          if (mountedRef.current) {
            if (lastCartIdRef.current !== cartId || lastCartItemsCountRef.current !== itemsCount) {
              lastCartIdRef.current = cartId;
              lastCartItemsCountRef.current = itemsCount;
              cartInitializedRef.current = true;

              setCart(cartData);
              setCartReady(true);
            } else {
              cartInitializedRef.current = true;
              setCartReady(true);
            }
          }
        } else {
          if (mountedRef.current) {
            lastCartIdRef.current = null;
            lastCartItemsCountRef.current = 0;
            cartInitializedRef.current = true;
            setCart(null);
            setCartReady(true); // Mark as ready even if empty to prevent re-fetch
            setError('Failed to load cart');
    }
        }
      } catch (err) {
        console.error('Error fetching cart:', err);
        
        // Parse error message (might be JSON string)
        let errorMessage = err?.message || err?.error || '';
        try {
          if (typeof errorMessage === 'string' && errorMessage.startsWith('{')) {
            const parsed = JSON.parse(errorMessage);
            errorMessage = parsed.error || parsed.message || errorMessage;
          }
        } catch (e) {
          // Not JSON, use as is
        }
        
        // Check if it's a version conflict error - if so, try to recover
        const isVersionConflict = errorMessage.includes('No matching document found') || 
                                  errorMessage.includes('version') ||
                                  errorMessage.includes('modifiedPaths');
        
        if (isVersionConflict && mountedRef.current) {
          // For version conflicts, try to fetch cart again after a short delay
          setTimeout(async () => {
            try {
              const retryResponse = await cartService.getCart();
              if (retryResponse.success && retryResponse.data) {
                const cartData = {
                  ...retryResponse.data,
                  items: Array.isArray(retryResponse.data.items) ? retryResponse.data.items : []
                };
                lastCartIdRef.current = cartData._id;
                lastCartItemsCountRef.current = cartData.items.length;
                cartInitializedRef.current = true;
                setCart(cartData);
                setCartReady(true);
                setError(null);
                return;
              }
            } catch (retryErr) {
              console.error('Error retrying cart fetch:', retryErr);
            }
            
            // If retry failed, set error
            if (mountedRef.current) {
              setError('Cart data conflict. Please refresh the page.');
              cartInitializedRef.current = true;
              setCartReady(true);
            }
          }, 500);
          return;
        }
        
        if (mountedRef.current) {
          setError(err.message || 'Failed to load cart');
          cartInitializedRef.current = true; // Mark as initialized even on error
          setCartReady(true); // Mark as ready to prevent re-fetch
        }
      } finally {
        fetchingRef.current = false;
        if (mountedRef.current) {
          setLoading(false);
        }
      }
    };

    // CRITICAL: Only fetch once - check all conditions
    const shouldFetch = !fetchingRef.current && 
                       !cartInitializedRef.current && 
                       (!cart || !cart._id) &&
                       isAuthenticated;

    if (shouldFetch) {
      fetchCart();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated]); // Only depend on isAuthenticated - prevent re-fetches

  // Fetch cart summary from API
  const fetchCartSummary = async () => {
    if (!isAuthenticated || !cartReady || !cart) {
      return;
    }

    try {
      setLoadingSummary(true);
      const token = localStorage.getItem('authToken');
      
      if (!token) {
        console.warn('No auth token found for cart summary');
        return;
      }

      const response = await fetch(`${getApiBaseUrl()}/cart/summary`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch cart summary');
      }

      const result = await response.json();
      
      if (result.data && mountedRef.current) {
        setCartSummary(result.data);
      }
    } catch (error) {
      console.error('Error fetching cart summary:', error);
      // Don't set error state, just log it - fallback to manual calculation
    } finally {
      if (mountedRef.current) {
        setLoadingSummary(false);
      }
    }
  };

  // Fetch cart summary when cart is ready
  useEffect(() => {
    if (isAuthenticated && cartReady && cart) {
      fetchCartSummary();
    }
  }, [isAuthenticated, cartReady, cart?._id]); // eslint-disable-line react-hooks/exhaustive-deps

  // Helper function to extract image URL from various formats
  const getImageUrl = (imageData) => {
    if (!imageData) return null;
    
    // If it's a string, return it
    if (typeof imageData === 'string' && imageData.trim() !== '') {
      return imageData.replace('http://', 'https://');
    }
    
    // If it's an array, get first element
    if (Array.isArray(imageData) && imageData.length > 0) {
      const firstImg = imageData[0];
      if (typeof firstImg === 'string') {
        return firstImg.replace('http://', 'https://');
      }
      if (firstImg && typeof firstImg === 'object') {
        return firstImg.url || firstImg.fullPath || firstImg.src || firstImg.path || null;
      }
    }
    
    // If it's an object, extract URL
    if (imageData && typeof imageData === 'object') {
      return imageData.url || imageData.fullPath || imageData.src || imageData.path || null;
    }
    
    return null;
  };

  // Determine which cart to use (API cart for authenticated, guest cart for visitors)
  // IMPORTANT: Prevent duplicate renders by using stable references
  // CRITICAL: Only return items if cart is ready (prevents flash of partial data)
  const bagItems = React.useMemo(() => {
    // For authenticated users, wait until cart is ready
    if (isAuthenticated && !cartReady) {
      return [];
    }

    let computedItems = [];

    if (isAuthenticated) {
      if (cartReady && cart?.items && Array.isArray(cart.items) && cart.items.length > 0) {
        const seenItemIds = new Set();
        const uniqueItems = cart.items.filter(item => {
          const itemId = item._id;
          if (!itemId) return false;
          if (seenItemIds.has(itemId)) {
            console.warn('⚠️ Duplicate item detected:', itemId, item.product?.name);
            return false;
          }
          seenItemIds.add(itemId);
          return true;
        });

        computedItems = uniqueItems.map(item => {
          const productImage = item.product?.images || item.product?.image;
          const imageUrl = getImageUrl(productImage);

          return {
            itemId: item._id,
            id: item.product._id || item.product.id,
            name: item.product.name,
            price: item.price,
            originalPrice: item.originalPrice || item.product.originalPrice,
            image: imageUrl,
            selectedSize: item.size,
            selectedColor: item.color,
            quantity: item.quantity,
            category: item.product.category,
            subcategory: item.product.subcategory,
            total: item.total
          };
        });
      }
    } else if (Array.isArray(guestBagItems) && guestBagItems.length > 0) {
      const seenItemIds = new Set();
      const uniqueItems = guestBagItems.filter(item => {
        const itemId = item.itemId || item.id || item._id;
        if (!itemId) return false;
        if (seenItemIds.has(itemId)) {
          console.warn('⚠️ Duplicate guest item:', itemId);
          return false;
        }
        seenItemIds.add(itemId);
        return true;
      });

      computedItems = uniqueItems.map(item => ({
        ...item,
        image: getImageUrl(item.image) || item.image
      }));
    }

    return computedItems;
    // Depend on full `cart` so quantity/subtotal updates always recompute even if `items`
    // keeps the same array reference from the API.
  }, [isAuthenticated, cartReady, cart, guestBagItems]);


  const bagTotal = isAuthenticated
    ? (cart?.subtotal || 0)
    : guestBagTotal;

  // Strict items subtotal (what user actually sees in cart rows) - no hidden tax/fees.
  const itemSubtotal = React.useMemo(() => {
    if (!bagItems || bagItems.length === 0) return 0;
    return bagItems.reduce(
      (sum, item) => sum + ((Number(item.price) || 0) * (Number(item.quantity) || 1)),
      0
    );
  }, [bagItems]);

  // Calculate total original amount (before sale discount)
  const totalOriginalAmount = React.useMemo(() => {
    if (!bagItems || bagItems.length === 0) return 0;
    return bagItems.reduce((sum, item) => {
      const price = Number(item.price) || 0;
      const originalPrice = Number(item.originalPrice);
      const unit = originalPrice > price ? originalPrice : price;
      return sum + (unit * (Number(item.quantity) || 1));
    }, 0);
  }, [bagItems]);

  // Sale discount shown in summary (difference between original and effective line prices)
  const saleDiscount = React.useMemo(
    () => Math.max(0, totalOriginalAmount - itemSubtotal),
    [totalOriginalAmount, itemSubtotal]
  );

  // Calculate sale discount percentage
  const saleDiscountPercent = totalOriginalAmount > 0 
    ? Math.round((saleDiscount / totalOriginalAmount) * 100) 
    : 0;

  // Calculate coupon discount
  const couponDiscount = React.useMemo(() => {
    if (!isAuthenticated) return 0;
    const beforeCoupon = Number(cart?.subtotal) || 0;
    const afterCoupon = Number(cart?.total) || beforeCoupon;
    // Coupon should reduce payable amount only; never negative.
    return Math.max(0, beforeCoupon - afterCoupon);
  }, [isAuthenticated, cart]);

  const discountAmount = saleDiscount + couponDiscount;

  // Calculate subtotal (items only, before shipping) - no hidden calculations.
  const subtotal = React.useMemo(() => {
    if (itemSubtotal > 0 || bagItems.length > 0) return itemSubtotal;
    return bagTotal;
  }, [itemSubtotal, bagItems.length, bagTotal]);

  const deliveryInfo = React.useMemo(
    () => computeShippingFromSubtotal(subtotal, storeContact),
    [subtotal, storeContact]
  );
  const shipping = deliveryInfo.shipping;

  // Total original amount (before any discounts)
  const totalOriginalBeforeDiscount = React.useMemo(() => {
    return totalOriginalAmount;
  }, [totalOriginalAmount]);
  
  // Final total (after coupon + shipping; sale discount already reflected in item prices/subtotal)
  const total = React.useMemo(() => {
    const payableBeforeShipping = Math.max(0, subtotal - couponDiscount);
    return payableBeforeShipping + shipping;
  }, [subtotal, couponDiscount, shipping]);

  const bagGuestWhatsAppOrderUrl = React.useMemo(() => {
    if (isAuthenticated || !bagItems?.length) return '';
    const digits = getWhatsAppOrderPhoneDigits();
    if (!digits) return '';
    let msg = buildBagWhatsAppOrderMessage({
      user: null,
      bagItems,
      subtotal,
      saleDiscount,
      couponDiscount: 0,
      shipping,
      total,
      deliveryInfo,
      isGuest: true,
    });
    const maxRaw = 3800;
    if (msg.length > maxRaw) {
      msg =
        msg.slice(0, maxRaw - 60).trim() +
        '\n\n…(details truncated — see shopping bag on site)';
    }
    return buildWhatsAppUrl(digits, msg);
  }, [
    isAuthenticated,
    bagItems,
    subtotal,
    saleDiscount,
    shipping,
    total,
    deliveryInfo,
  ]);

  // Handle quantity update
  const handleUpdateQuantity = async (itemId, newQuantity) => {
    if (newQuantity <= 0) {
      handleRemoveItem(itemId);
      return;
    }

    if (!isAuthenticated) {
      // Guest user - use localStorage
      updateGuestQuantity(itemId, newQuantity);
      return;
    }

    // Authenticated user - use API
    try {
      setUpdatingItem(itemId);
      const response = await cartService.updateCartItem(itemId, newQuantity);
      
      if (response.success && response.data) {
        const rawItems = Array.isArray(response.data.items) ? response.data.items : [];
        const cartData = {
          ...response.data,
          items: rawItems.map((row) => ({ ...row }))
        };
        lastCartIdRef.current = cartData._id;
        lastCartItemsCountRef.current = cartData.items.length;
        setCart(cartData);
      } else {
        throw new Error(response.error || 'Failed to update quantity');
      }
    } catch (err) {
      console.error('Error updating quantity:', err);
      
      // Parse error message (might be JSON string)
      let errorMessage = err?.message || err?.error || '';
      try {
        if (typeof errorMessage === 'string' && errorMessage.startsWith('{')) {
          const parsed = JSON.parse(errorMessage);
          errorMessage = parsed.error || parsed.message || errorMessage;
        }
      } catch (e) {
        // Not JSON, use as is
      }
      
      // Check if it's a version conflict error
      const isVersionConflict = errorMessage.includes('No matching document found') || 
                                errorMessage.includes('version') ||
                                errorMessage.includes('modifiedPaths');
      
      if (isVersionConflict) {
        // Refresh cart and retry once
        try {
          const cartResponse = await cartService.getCart();
          if (cartResponse.success && cartResponse.data) {
            const cartData = {
              ...cartResponse.data,
              items: Array.isArray(cartResponse.data.items) ? cartResponse.data.items : []
            };
            lastCartIdRef.current = cartData._id;
            lastCartItemsCountRef.current = cartData.items.length;
            setCart(cartData);
            // Silently handle - cart is now refreshed
            return;
          }
        } catch (refreshErr) {
          console.error('Error refreshing cart:', refreshErr);
        }
      }
      
      // Only show alert for non-version-conflict errors
      if (!isVersionConflict) {
        alert(err.message || 'Failed to update quantity. Please try again.');
      }
    } finally {
      setUpdatingItem(null);
    }
  };

  // Handle remove item
  const handleRemoveItem = (itemId) => {
    setItemToRemove(itemId);
    setShowConfirmation(true);
  };

  const confirmRemoveItem = async () => {
    if (!itemToRemove) {
      setShowConfirmation(false);
      return;
    }

    if (!isAuthenticated) {
      // Guest user - use localStorage
      removeGuestItem(itemToRemove);
      setItemToRemove(null);
      setShowConfirmation(false);
      return;
    }

    // Authenticated user - use API
    try {
      setRemovingItem(itemToRemove);
      const response = await cartService.removeFromCart(itemToRemove);
      
      if (response.success && response.data) {
        const rawItems = Array.isArray(response.data.items) ? response.data.items : [];
        const cartData = {
          ...response.data,
          items: rawItems.map((row) => ({ ...row }))
        };
        lastCartIdRef.current = cartData._id;
        lastCartItemsCountRef.current = cartData.items.length;
        setCart(cartData);
      } else {
        throw new Error(response.error || 'Failed to remove item');
      }
    } catch (err) {
      console.error('Error removing item:', err);
      
      // Parse error message (might be JSON string)
      let errorMessage = err?.message || err?.error || '';
      try {
        if (typeof errorMessage === 'string' && errorMessage.startsWith('{')) {
          const parsed = JSON.parse(errorMessage);
          errorMessage = parsed.error || parsed.message || errorMessage;
        }
      } catch (e) {
        // Not JSON, use as is
      }
      
      // Check if it's a version conflict error
      const isVersionConflict = errorMessage.includes('No matching document found') || 
                                errorMessage.includes('version') ||
                                errorMessage.includes('modifiedPaths');
      
      if (isVersionConflict) {
        // Refresh cart and retry once
        try {
          const cartResponse = await cartService.getCart();
          if (cartResponse.success && cartResponse.data) {
            const cartData = {
              ...cartResponse.data,
              items: Array.isArray(cartResponse.data.items) ? cartResponse.data.items : []
            };
            lastCartIdRef.current = cartData._id;
            lastCartItemsCountRef.current = cartData.items.length;
            setCart(cartData);
            // Silently handle - cart is now refreshed
            return;
          }
        } catch (refreshErr) {
          console.error('Error refreshing cart:', refreshErr);
        }
      }
      
      // Only show alert for non-version-conflict errors
      if (!isVersionConflict) {
        alert(err.message || 'Failed to remove item. Please try again.');
      }
    } finally {
      setRemovingItem(null);
      setItemToRemove(null);
      setShowConfirmation(false);
    }
  };

  const cancelRemoveItem = () => {
    setItemToRemove(null);
    setShowConfirmation(false);
  };

  // Loading state (only for authenticated users)
  if (loading && isAuthenticated) {
    return (
      <div className="bag-page">
        <div className="container">
          <SiteLoading
            title="Loading Your Cart"
            subtitle="Fetching your items..."
            icon="🛒"
            variant="full"
          />
        </div>
      </div>
    );
  }

  // Error state (only for authenticated users)
  if (error && isAuthenticated) {
    return (
      <div className="bag-page">
        <div className="container">
          <div className="bag-error">
            <div className="error-icon">⚠️</div>
            <h2>Error Loading Cart</h2>
            <p>{error}</p>
            <button 
              className="retry-btn"
              onClick={() => window.location.reload()}
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  // Empty cart state - check both guest and authenticated carts
  // For authenticated: ONLY check cart.items from API (no guest cart fallback)
  // CRITICAL: Only check if cart is ready to prevent flash
  const hasItems = isAuthenticated 
    ? (cartReady && cart?.items && Array.isArray(cart.items) && cart.items.length > 0)
    : (guestBagItems && Array.isArray(guestBagItems) && guestBagItems.length > 0);

  // Don't show empty state while loading (authenticated) or if guest items might still be loading
  // For authenticated users, also wait for cartReady to prevent flash
  const isStillLoading = isAuthenticated 
    ? (loading || !cartReady)
    : (loading && !guestBagItems);

  if (!hasItems && !isStillLoading) {
    return (
      <div className="bag-page">
        <div className="container">
          <div className="empty-bag">
            <div className="empty-bag-icon">🛍️</div>
            <h2>Your Bag is Empty</h2>
            <p>Looks like you haven't added anything to your bag yet.</p>
            <Link to="/" className="continue-shopping-btn">
              Continue Shopping
            </Link>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bag-page">
      <div className="container">
        <div className="bag-header">
          <h1>Shopping Bag</h1>
          <p>{(bagItems?.length || 0)} item{(bagItems?.length || 0) !== 1 ? 's' : ''} in your bag</p>
        </div>

        {/* Guest: existing account → sign in (no guest-cart expiry copy) */}
        {!isAuthenticated && hasItems && (
          <div className="bag-guest-login-bar">
            <p className="bag-guest-login-bar__text">Already have an account?</p>
            <button
              type="button"
              className="bag-guest-login-bar__btn"
              onClick={() => navigate('/login')}
            >
              Log in
            </button>
          </div>
        )}

        {!isAuthenticated && hasItems && (
          <div className="bag-guest-account-wrap">
            <CheckoutGuestAccountForm
              onSuccess={() => {
                navigate('/checkout');
              }}
            />
          </div>
        )}

        <div className="bag-content">
          <div className="bag-items-section">
            <h2 className="bag-items-section-title">Your Items</h2>
            <div className="bag-items" key={`bag-items-${isAuthenticated ? (cart?._id || 'no-cart') : 'guest'}-${bagItems?.length || 0}`}>
              {bagItems && Array.isArray(bagItems) && bagItems.length > 0 ? (() => {
                // Final deduplication before rendering - ensure unique keys
                const seenKeys = new Set();
                const uniqueBagItems = bagItems.filter(item => {
                  const key = item.itemId || item.id || item._id;
                  if (!key) {
                    console.warn('⚠️ Item without key for rendering:', item);
                    return false;
                  }
                  if (seenKeys.has(key)) {
                    console.warn('⚠️ Duplicate key in rendering:', key, item.name);
                    return false;
                  }
                  seenKeys.add(key);
                  return true;
                });

                return uniqueBagItems.map((item) => (
              <div key={`${item.itemId || item.id || item._id}-${item.selectedSize || ''}-${item.selectedColor || ''}`} className="bag-item">
                <div className="item-image">
                  <img 
                    src={item.image || PLACEHOLDER_NO_IMAGE_SMALL} 
                    alt={item.name || 'Product'}
                    onError={(e) => {
                      if (e.target.src !== PLACEHOLDER_NO_IMAGE_SMALL) {
                        e.target.src = PLACEHOLDER_NO_IMAGE_SMALL;
                      }
                    }}
                    loading="lazy"
                  />
                </div>

                <div className="item-content">
                  <h3 className="item-name">{item.name}</h3>

                  <div className="item-specs">
                    {item.selectedColor && !isDefaultColorLabel(item.selectedColor) && (
                      <span className="spec">{item.selectedColor} Colour</span>
                    )}
                    {item.selectedSize && (
                      <span className="spec">Size: {item.selectedSize}</span>
                    )}
                  </div>

                  <div className="item-price">
                    {item.originalPrice && item.originalPrice > item.price ? (
                      <>
                        <span className="current-price">PKR {item.price.toLocaleString()}</span>
                        <span className="original-price">PKR {item.originalPrice.toLocaleString()}</span>
                      </>
                    ) : (
                      <span>PKR {item.price.toLocaleString()}</span>
                    )}
                  </div>

                  <div className="item-controls">
                    <div className="quantity-controls">
                      <button 
                        className="quantity-btn"
                        onClick={() => handleUpdateQuantity(item.itemId, item.quantity - 1)}
                        disabled={item.quantity <= 1 || updatingItem === item.itemId || removingItem === item.itemId}
                      >
                        {updatingItem === item.itemId ? '...' : '−'}
                      </button>
                      <span className="quantity">{item.quantity}</span>
                      <button 
                        className="quantity-btn"
                        onClick={() => handleUpdateQuantity(item.itemId, item.quantity + 1)}
                        disabled={item.quantity >= 10 || updatingItem === item.itemId || removingItem === item.itemId}
                      >
                        {updatingItem === item.itemId ? '...' : '+'}
                      </button>
                    </div>

                    <button
                      type="button"
                      className="remove-btn delete-item-btn bag-item-remove-btn"
                      onClick={() => handleRemoveItem(item.itemId)}
                      disabled={removingItem === item.itemId || updatingItem === item.itemId}
                      title="Remove from bag"
                      aria-label="Remove item from bag"
                    >
                      {removingItem === item.itemId ? (
                        <i className="fas fa-spinner fa-spin" aria-hidden />
                      ) : (
                        <>
                          <i className="fas fa-trash-alt" aria-hidden />
                          <span>Remove</span>
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
              ));
              })() : (
                <div style={{ padding: '20px', textAlign: 'center', color: '#666' }}>
                  No items in your bag
                </div>
              )}
            </div>
          </div>

          <div className="checkout-right">
            <div className="order-summary-card">
              <h2 className="summary-title">Order Summary</h2>
              
              <div className="summary-content">
                {/* 1. Total Amount - First (Original amount before discount) */}
                <div className="summary-total">
                  <span>Total Amount</span>
                  <span>PKR {totalOriginalBeforeDiscount.toLocaleString()}</span>
                </div>

                {/* 2. Discount - Second - Always show if there's any discount */}
                {saleDiscount > 0 && (
                  <div className="summary-line discount">
                    <span>
                      <i className="fas fa-tag"></i> Sale Discount {saleDiscountPercent > 0 && `(${saleDiscountPercent}% OFF)`}
                    </span>
                    <span className="summary-value">-PKR {saleDiscount.toLocaleString()}</span>
                  </div>
                )}

                {couponDiscount > 0 && (
                  <div className="summary-line discount">
                    <span>
                      <i className="fas fa-ticket-alt"></i> Coupon Discount
                    </span>
                    <span className="summary-value">-PKR {couponDiscount.toLocaleString()}</span>
                  </div>
                )}

                {/* 3. Shipping - Third */}
                <div className="summary-line summary-shipping">
                  <span>
                    <i className="fas fa-truck"></i> Shipping
                  </span>
                  <span className="summary-value">
                    {shipping === 0 ? (
                      <span className="free-shipping">FREE</span>
                    ) : (
                      `PKR ${shipping.toLocaleString()}`
                    )}
                  </span>
                </div>

                <div className="summary-divider"></div>

                {/* 4. Subtotal (including shipping) */}
                <div className="summary-line summary-subtotal">
                  <span>Subtotal (incl. shipping)</span>
                  <span className="summary-value">PKR {total.toLocaleString()}</span>
                </div>
              </div>

              <div className="checkout-actions">
                {!isAuthenticated && bagGuestWhatsAppOrderUrl && (
                  <a
                    href={bagGuestWhatsAppOrderUrl}
                    className="checkout-wa-order-btn bag-guest-wa-order-btn"
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <i className="fab fa-whatsapp" aria-hidden />
                    Order via WhatsApp
                  </a>
                )}

                {!isAuthenticated && (
                  <p className="bag-guest-checkout-hint">
                    Create your account and delivery address above, then continue to checkout.
                  </p>
                )}

                <button 
                  className="checkout-btn"
                  disabled={!isAuthenticated}
                  title={
                    !isAuthenticated
                      ? 'Complete account and delivery on this page first'
                      : undefined
                  }
                  onClick={async () => {
                    if (!isAuthenticated) return;
                    try {
                      if (isAuthenticated && (!cart || !cartReady)) {
                        await new Promise((resolve) => setTimeout(resolve, 500));
                      }
                    } catch {
                      /* ignore */
                    }
                    navigate('/checkout');
                  }}
                >
                  PROCEED TO CHECKOUT
                </button>

                <Link to="/" className="bag-continue-shopping-link">
                  Continue Shopping
                </Link>

                <div className="shipping-info">
                  {deliveryInfo.freeDeliveryEnabled ? (
                    <>
                      <p>
                        📦 Free delivery on orders PKR{' '}
                        {deliveryInfo.threshold.toLocaleString()} or more (otherwise standard shipping
                        PKR {deliveryInfo.standardShippingFee.toLocaleString()}).
                      </p>
                      {!deliveryInfo.qualified && deliveryInfo.amountToAdd > 0 && (
                        <p className="free-delivery-hint">
                          Add PKR {deliveryInfo.amountToAdd.toLocaleString()} more to unlock free
                          delivery on this order.
                        </p>
                      )}
                      {deliveryInfo.qualified && (
                        <p className="free-delivery-hint free-delivery-qualified">
                          You qualify for free delivery on this order.
                        </p>
                      )}
                    </>
                  ) : (
                    <p>
                      📦 Standard shipping: PKR {deliveryInfo.standardShippingFee.toLocaleString()}
                    </p>
                  )}
                  <p>🚚 Delivery in 2-3 business days</p>
                  <p>💯 100% authentic products</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Confirmation Modal */}
        <ConfirmationModal
          isOpen={showConfirmation}
          onClose={cancelRemoveItem}
          onConfirm={confirmRemoveItem}
          title="Remove Item"
          message="Are you sure you want to remove this item from your shopping bag?"
        />

      </div>
    </div>
  );
};

export default BagPage; 
